import { useState, useCallback, useRef } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { TopBar } from './TopBar'
import IconRail from './IconRail'
import FlyoutMenu from './FlyoutMenu'
import SectionSidebar from './SectionSidebar'
import { Footer } from './Footer'
import { ErrorBoundary } from '../ErrorBoundary'
import { useShellNavigation } from '../../hooks/useShellNavigation'
import { isFixedViewportRoute } from './fixedViewportRoutes'

const HOVER_CLOSE_DELAY = 300

export function AppLayout() {
  const {
    activeSection,
    visibleMainNav,
    sidebarCollapsed,
    toggleSidebar,
    hoveredRailItem,
    setHoveredRailItem,
    navigateToSection,
    projectId,
  } = useShellNavigation()

  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null)
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const hoveredItem = hoveredRailItem
    ? visibleMainNav.find(item => item.id === hoveredRailItem) ?? null
    : null

  // Cancel any pending close timeout
  const cancelHoverTimeout = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
  }, [])

  // Start a delayed close
  const startHoverTimeout = useCallback(() => {
    cancelHoverTimeout()
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredRailItem(null)
      setAnchorRect(null)
    }, HOVER_CLOSE_DELAY)
  }, [cancelHoverTimeout, setHoveredRailItem])

  // Rail item hovered: cancel timeout, set item
  const handleHoverItem = useCallback((id: string | null, rect?: DOMRect) => {
    if (id) {
      cancelHoverTimeout()
      setHoveredRailItem(id)
      setAnchorRect(rect ?? null)
    } else {
      // Mouse left the rail — start delayed close
      startHoverTimeout()
    }
  }, [cancelHoverTimeout, startHoverTimeout, setHoveredRailItem])

  // Flyout hovered: cancel timeout (keep open)
  const handleFlyoutEnter = useCallback(() => {
    cancelHoverTimeout()
  }, [cancelHoverTimeout])

  // Flyout left: start delayed close
  const handleFlyoutLeave = useCallback(() => {
    startHoverTimeout()
  }, [startHoverTimeout])

  const hasSectionSidebar = Boolean(
    activeSection?.groups && activeSection.groups.length > 0
  )

  const location = useLocation()
  const fixedViewport = isFixedViewportRoute(location.pathname)

  return (
    <div className={`bg-light-body flex flex-col ${fixedViewport ? 'h-screen overflow-hidden' : 'min-h-screen'}`}>
      <TopBar />
      <div className={`flex flex-1 ${fixedViewport ? 'min-h-0' : ''}`}>
        <IconRail
          visibleMainNav={visibleMainNav}
          activeSection={activeSection}
          hoveredRailItem={hoveredRailItem}
          onHoverItem={handleHoverItem}
          onClickItem={navigateToSection}
        />
        {hasSectionSidebar && activeSection && (
          <SectionSidebar
            section={activeSection}
            collapsed={sidebarCollapsed}
            onToggle={toggleSidebar}
            projectId={projectId}
          />
        )}
        <div className="flex-1 flex flex-col min-w-0">
          <main className={`flex-1 px-4 py-4 ${fixedViewport ? 'min-h-0 overflow-hidden' : ''}`}>
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </main>
          <Footer />
        </div>
      </div>

      {hoveredItem && (hoveredItem.groups || hoveredItem.children) && (
        <FlyoutMenu
          item={hoveredItem}
          anchorRect={anchorRect}
          onClose={() => setHoveredRailItem(null)}
          onMouseEnter={handleFlyoutEnter}
          onMouseLeave={handleFlyoutLeave}
          projectId={projectId}
        />
      )}
    </div>
  )
}
